import React, {useMemo, useState} from 'react';
import {
  DashboardLayout,
  PrimaryButton,
  SearchInput,
  Filter,
  FilterButton,
} from '../../../components';
import {baasBalance, baasMobile} from '../../../assets/images/images';
import {useNavigate} from 'react-router-dom';
import {useQuery} from 'react-query';
import {fetchBAASCompanyWallets} from '../../../requests/queries/baas';
import {useSelector} from 'react-redux';
import {useBaasExport} from './hooks/useBAASExport';
import {usePermissions} from '../../../hooks/useSidbarPermissions';
import {OverviewCards} from '../../../components/OverviewCards';
import {buildTableCardData} from '../../../helpers/buildTableCardData';
import BaasWalletTable from './components/BaasWalletTable';
import {
  Constants,
  filterOutEmptyValues,
  generateQueryParams,
} from '../../../helpers';
import AppliedFilters from 'components/AppliedFilters';
import moment from 'moment';

const initialFilters = {
  start: '',
  end: '',
  flows: '',
};

function BaasWallets() {
  const [openFilter, setOpenFilter] = useState(false);
  const navigate = useNavigate();
  const [filters, setFilters] = useState(initialFilters);
  const [searchText, setSearchText] = useState('');
  const {userDetails} = useSelector(state => state?.auth);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filterProps, setFilterProps] = useState(null);

  const [query, setQuery] = useState({
    page: 1,
    length: 10,
  });

  const {
    baasPagePermissions: {canExportWallets},
  } = usePermissions();

  const formatDate = date => (date ? moment(date).format('YYYY-MM-DD') : '');

  const params = useMemo(
    () => ({
      ...query,
      company_id: userDetails && userDetails?.company?.id,
      search: searchText,
      start_date: formatDate(filters?.start),
      end_date: formatDate(filters?.end),
    }),
    [filters?.end, filters?.start, query, searchText, userDetails],
  );

  const {handleExport, exportData} = useBaasExport({
    params,
    apiFunc: fetchBAASCompanyWallets,
  });

  const {data: company_wallets, isLoading} = useQuery(
    ['baas-wallet', {params}],
    () => fetchBAASCompanyWallets({params}),
    {
      meta: {preventGlobalError: true},
      enabled: !exportData,
    },
  );

  const wallets = useMemo(
    () => !isLoading && company_wallets && company_wallets?.entity,
    [company_wallets, isLoading],
  );

  const handleFilter = (selectedOptions, shouldDownload = false) => {
    const filteredOptions = filterOutEmptyValues(selectedOptions);
    const queryParams = generateQueryParams({
      ...filteredOptions,
      start: formatDate(filteredOptions.start),
      end: formatDate(filteredOptions.end),
    });

    let baseUrl = '';
    if (queryParams) {
      baseUrl += `?${queryParams}`;
    }

    if (shouldDownload) {
      filteredOptions.download = true;
      setFilters({
        ...filters,
        ...filteredOptions,
      });
    } else {
      navigate(baseUrl);
    }

    setFiltersApplied(true);
    setFilters(filteredOptions);
    setOpenFilter(false);
    setFiltersApplied(
      Object.values(filteredOptions).some(value => value) ||
        searchText.length > 0,
    );
  };

  const handleResetFilter = () => {
    navigate('');
    setFilters(initialFilters);
    setOpenFilter(false);
    setSearchText('');
    setFiltersApplied(false);
  };

  const handlePageChange = page => {
    setQuery(prev => ({...prev, page}));
    const searchParams = new URLSearchParams(document.location.search);
    searchParams.set('page', page);
    const newSearchParams = searchParams.toString();
    navigate(`?${newSearchParams}`);
  };

  const handleLimitChange = length => {
    setQuery(prev => ({...prev, length, page: 1}));
  };

  const handleViewWallet = wallet => {
    navigate(
      Constants.PAGES_URL.BAAS_ACCOUNT_DETAIL?.replace(
        ':slug',
        wallet?.wallet_id,
      ),
    );
  };

  const handleFilterStateChange = props => {
    setFilterProps(props);
    setFiltersApplied(
      Object.values(filters).some(value => value) || searchText.length > 0,
    );
  };

  const tableCardData = buildTableCardData({
    count: wallets?.total,
    title: 'Total Wallet Created',
    icon: baasBalance,
  });

  return (
    <DashboardLayout
      bg="bg-white80"
      topPadding="pt-8"
      xlLeftMargin="xl:ml-12"
      breadCrumbs={
        <div className="flex items-center">
          <img src={baasMobile} alt="" />
          <p className="ml-2 -mb-1">Wallets</p>
        </div>
      }
    >
      <div className="flex items-start lg:items-center lg:flex-row flex-col gap-5 justify-between p-5 lg:p-0">
        <div className="flex items-center gap-4 flex-wrap">
          <p className="text-grey font-medium text-[18px]">Wallets</p>
          {filtersApplied && filterProps && (
            <AppliedFilters {...filterProps} isMainPage={true} />
          )}
        </div>

        <div className="flex items-center gap-2">
          <SearchInput
            onChange={value => setSearchText(value)}
            className="sm:w-[277px] rounded-lg outline-[#eceff3]"
          />
          <FilterButton
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
            className="h-[48px] justify-center w-[130px]"
          />
          <PrimaryButton
            buttonText="Export"
            className="w-[121px]"
            onClick={handleExport}
            loading={exportData}
            disabled={!canExportWallets}
          />
        </div>
      </div>

      <Filter
        openFilter={openFilter}
        setOpenFilter={setOpenFilter}
        handleFilter={handleFilter}
        resetUrl={handleResetFilter}
        onFilterStateChange={handleFilterStateChange}
        filterOptions={[]}
      />

      <div className="mt-8">
        <OverviewCards
          cardData={tableCardData}
          overviewLoading={isLoading}
          className={`grid grid-cols-2 md:grid-cols-3 xl:grid-cols-1 gap-2 lg:gap-4 flex-wrap my-5`}
        />

        <BaasWalletTable
          wallets={wallets?.wallets || []}
          isLoading={isLoading}
          onViewWallet={handleViewWallet}
          className="mt-6"
          showEdit={false}
          totalPages={wallets?.total_pages || 1}
          currentPage={Number(wallets?.current_page) || 1}
          onPageChange={handlePageChange}
          limit={query.length}
          onLimitChange={handleLimitChange}
          totalItems={wallets?.current_page || 0}
          filtersApplied={filtersApplied}
          searchText={searchText}
        />
      </div>
    </DashboardLayout>
  );
}

export default BaasWallets;
